import {computed} from 'vue'
import useI18nExtended from '~/composables/usei18nExtended'
import {useDynamicsLeadCapture} from '~/composables/useDynamicsLeadCapture'

/**
 * Industry type codes for Dynamics 365 lead capture
 * Primary industry categorization (first dropdown)
 */
export const INDUSTRY_TYPE_CODES = Object.freeze({
  Automotive: 419260000,
  NonAutomotive: 419260001,
  AutomotiveAftermarket: 419260002
})

/**
 * Specific industry codes for Dynamics 365 lead capture
 * Detailed industries shown when Non-Automotive is selected (second dropdown)
 */
export const SPECIFIC_INDUSTRY_CODES = Object.freeze({
  Aerospace: 419260000,
  Agriculture: 419260001,
  Appliances: 419260002,
  Automotive: 419260003,
  AutomotiveAftermarket: 419260004,
  BuildingEngineering: 419260005,
  Chemical: 419260024,
  CommercialMarine: 419260006,
  Construction: 419260007,
  Distribution: 419260027,
  EnergyGeneration: 419260008,
  EnergyStorage: 419260009,
  EnergyTransmission: 419260010,
  FluidHandling: 419260011,
  FluidTechnology: 419260012,
  FoodBeverage: 419260025,
  Forklifts: 419260013,
  Hydrogen: 419260014,
  IndustrialMachines: 419260015,
  MetalProcessing: 419260016,
  Pharmaceutical: 419260026,
  PowerTools: 419260017,
  PowerGeneration: 419260018,
  PowerTransmission: 419260019,
  Railway: 419260020,
  Recreational: 419260021,
  Robotics: 419260022,
  TunnelBoring: 419260023
})

/**
 * Provides standardized dropdown options for lead capture forms
 * All options use Dynamics 365 GUIDs and partnerHub translations
 * 
 * @returns Object containing dropdown option arrays
 */
export const useFormDropdowns = () => {
  const i18n = useI18nExtended()
  const dynamicsCapture = useDynamicsLeadCapture()

  /**
   * Country dropdown options with D365 GUIDs
   * Complete list of all countries from Dynamics 365
   */
  const countryOptions = computed(() => [
    {text: 'Guernsey (Channel Islands)', value: '7facb1dc-a5d1-ea11-a812-000d3ab38e8b'},
    {text: 'Isle of Man', value: '84acb1dc-a5d1-ea11-a812-000d3ab38e8b'},
    {text: 'Jersey', value: '86acb1dc-a5d1-ea11-a812-000d3ab38e8b'},
    {text: 'Republic of the Congo', value: 'cfacb1dc-a5d1-ea11-a812-000d3ab38e8b'},
    {text: 'Andorra', value: '0173a636-08bb-e611-80f4-00155d85c140'},
    {text: 'United Arab Emirates', value: '0273a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Afghanistan', value: '0373a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Antigua and Barbuda', value: '0473a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Anguilla', value: '0573a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Albania', value: '0673a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Armenia', value: '0773a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Angola', value: '0873a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Antarctica', value: '0973a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Argentina', value: '0a73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'American Samoa', value: '0b73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Austria', value: '0c73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Australia', value: '0d73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Aruba', value: '0e73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Azerbaijan', value: '0f73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Bosnia and Herzegovina', value: '1073a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Barbados', value: '1173a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Bangladesh', value: '1273a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Belgium', value: '1373a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Burkina Faso', value: '1473a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Bulgaria', value: '1573a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Bahrain', value: '1673a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Burundi', value: '1773a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Benin', value: '1873a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Bermuda', value: '1973a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Brunei Darussalam', value: '1a73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Bolivia', value: '1b73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Brazil', value: '1c73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Bahamas', value: '1d73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Bhutan', value: '1e73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Bouvet Islands', value: '1f73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Botswana', value: '2073a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Belarus', value: '2173a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Belize', value: '2273a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Canada', value: '2373a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Coconut Islands', value: '2473a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Democratic Republic of the Congo', value: '2573a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Central African Republic', value: '2673a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Switzerland', value: '2873a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Cote d\'Ivoire', value: '2973a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Cook Islands', value: '2a73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Chile', value: '2b73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Cameroon', value: '2c73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'China', value: '2d73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Colombia', value: '2e73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Costa Rica', value: '2f73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Cuba', value: '3073a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Cape Verde', value: '3173a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Christmas Islnd', value: '3273a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Cyprus', value: '3373a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Czech Republic', value: '3473a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Germany', value: '3573a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Djibouti', value: '3673a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Denmark', value: '3773a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Dominica', value: '3873a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Dominican Republic', value: '3973a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Algeria', value: '3a73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Ecuador', value: '3b73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Estonia', value: '3c73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Egypt', value: '3d73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'West Sahara', value: '3e73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Eritrea', value: '3f73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Spain', value: '4073a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Ethiopia', value: '4173a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Finland', value: '4273a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Fiji', value: '4373a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Falkland Islands', value: '4473a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Micronesia', value: '4573a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Faroe Islands', value: '4673a636-08bb-e611-80f4-00155d85c140'},
    {text: 'France', value: '4773a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Gabon', value: '4873a636-08bb-e611-80f4-00155d85c140'},
    {text: 'United Kingdom', value: '4973a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Grenada', value: '4a73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Georgia', value: '4b73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'French Guyana', value: '4c73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Ghana', value: '4d73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Gibraltar', value: '4e73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Greenland', value: '4f73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Gambia', value: '5073a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Guinea', value: '5173a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Guadeloupe', value: '5273a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Equatorial Guinea', value: '5373a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Greece', value: '5473a636-08bb-e611-80f4-00155d85c140'},
    {text: 'South Georgia and the Southern Sandwich Islands', value: '5573a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Guatemala', value: '5673a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Guam', value: '5773a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Guinea-Bissau', value: '5873a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Guyana', value: '5973a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Hong Kong', value: '5a73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Heard and McDonald Islands', value: '5b73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Honduras', value: '5c73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Croatia', value: '5d73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Haiti', value: '5e73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Hungary', value: '5f73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Indonesia', value: '6073a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Ireland', value: '6173a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Israel', value: '6273a636-08bb-e611-80f4-00155d85c140'},
    {text: 'India', value: '6373a636-08bb-e611-80f4-00155d85c140'},
    {text: 'British Indian Ocean Territory', value: '6473a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Iraq', value: '6573a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Iran', value: '6673a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Iceland', value: '6773a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Italy', value: '6873a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Jamaica', value: '6973a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Jordan', value: '6a73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Japan', value: '6b73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Kenya', value: '6c73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Kyrgyzstan', value: '6d73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Cambodia', value: '6e73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Kiribati', value: '6f73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Comoros', value: '7073a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Saint Kitts and Nevis', value: '7173a636-08bb-e611-80f4-00155d85c140'},
    {text: 'North Korea', value: '7273a636-08bb-e611-80f4-00155d85c140'},
    {text: 'South Korea', value: '7373a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Kuwait', value: '7473a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Cayman Islands', value: '7573a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Kazakhstan', value: '7673a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Laos', value: '7773a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Lebanon', value: '7873a636-08bb-e611-80f4-00155d85c140'},
    {text: 'St. Lucia', value: '7973a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Liechtenstein', value: '7a73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Sri Lanka', value: '7b73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Liberia', value: '7c73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Lesotho', value: '7d73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Lithuania', value: '7e73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Luxembourg', value: '7f73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Latvia', value: '8073a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Libya', value: '8173a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Morocco', value: '8273a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Monaco', value: '8373a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Moldova', value: '8473a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Montenegro', value: '8573a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Madagascar', value: '8673a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Marshall Islands', value: '8773a636-08bb-e611-80f4-00155d85c140'},
    {text: 'North Macedonia', value: '8873a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Mali', value: '8973a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Burma', value: '8a73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Mongolia', value: '8b73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Macau', value: '8c73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'North Mariana Islands', value: '8d73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Martinique', value: '8e73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Mauretania', value: '8f73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Montserrat', value: '9073a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Malta', value: '9173a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Mauritius', value: '9273a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Maldives', value: '9373a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Malawi', value: '9473a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Mexico', value: '9573a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Malaysia', value: '9673a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Mozambique', value: '9873a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Namibia', value: '9973a636-08bb-e611-80f4-00155d85c140'},
    {text: 'New Caledonia', value: '9a73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Niger', value: '9b73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Norfolk Islands', value: '9c73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Nigeria', value: '9d73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Nicaragua', value: '9e73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Netherlands', value: '9f73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Norway', value: 'a073a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Nepal', value: 'a173a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Nauru', value: 'a273a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Niue', value: 'a373a636-08bb-e611-80f4-00155d85c140'},
    {text: 'New Zealand', value: 'a473a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Oman', value: 'a573a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Panama', value: 'a673a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Peru', value: 'a773a636-08bb-e611-80f4-00155d85c140'},
    {text: 'French Polynesia', value: 'a873a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Papua New Guinea', value: 'a973a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Philippines', value: 'aa73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Pakistan', value: 'ab73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Poland', value: 'ac73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'St. Pierre and Miquelon', value: 'ad73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Pitcairn Islands', value: 'ae73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Puerto Rico', value: 'af73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Palestine', value: 'b073a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Portugal', value: 'b173a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Palau', value: 'b273a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Paraguay', value: 'b373a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Qatar', value: 'b473a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Reunion', value: 'b573a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Romania', value: 'b673a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Serbia', value: 'b773a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Russian Federation', value: 'b873a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Rwanda', value: 'b973a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Saudi Arabia', value: 'ba73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Solomon Islands', value: 'bb73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Seychelles', value: 'bc73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Sudan', value: 'bd73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Sweden', value: 'be73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Singapore', value: 'bf73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Saint Helena', value: 'c073a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Slovenia', value: 'c173a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Svalbard', value: 'c273a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Slovakia', value: 'c373a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Sierra Leone', value: 'c473a636-08bb-e611-80f4-00155d85c140'},
    {text: 'San Marino', value: 'c573a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Senegal', value: 'c673a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Somalia', value: 'c773a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Suriname', value: 'c873a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Sao Tome and Principe', value: 'c973a636-08bb-e611-80f4-00155d85c140'},
    {text: 'El Salvador', value: 'ca73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Syria', value: 'cb73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Swaziland', value: 'cc73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Turks and Caicos Islands', value: 'cd73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Chad', value: 'ce73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'French S.Territ', value: 'cf73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Togo', value: 'd073a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Thailand', value: 'd173a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Tajikistan', value: 'd273a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Tokelau Islands', value: 'd373a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Turkmenistan', value: 'd473a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Tunisia', value: 'd573a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Tonga', value: 'd673a636-08bb-e611-80f4-00155d85c140'},
    {text: 'East Timor', value: 'd773a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Türkiye', value: 'd873a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Trinidad and Tobago', value: 'd973a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Tuvalu', value: 'da73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Taiwan', value: 'db73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Tanzania', value: 'dc73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Ukraine', value: 'dd73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Uganda', value: 'de73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'American Minor Outlying Islands', value: 'df73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'USA', value: 'e073a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Uruguay', value: 'e173a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Uzbekistan', value: 'e273a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Vatican City', value: 'e373a636-08bb-e611-80f4-00155d85c140'},
    {text: 'St. Vincent and the Grenadines', value: 'e473a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Venezuela', value: 'e573a636-08bb-e611-80f4-00155d85c140'},
    {text: 'British Virgin Islands', value: 'e673a636-08bb-e611-80f4-00155d85c140'},
    {text: 'American Virgin Islands', value: 'e773a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Vietnam', value: 'e873a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Vanuatu', value: 'e973a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Wallis and Futuna Islands', value: 'ea73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Samoa', value: 'eb73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Serbien', value: 'ed73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Yemen', value: 'ee73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Mayotte', value: 'ef73a636-08bb-e611-80f4-00155d85c140'},
    {text: 'South Africa', value: 'f073a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Zambia', value: 'f173a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Zimbabwe', value: 'f273a636-08bb-e611-80f4-00155d85c140'},
    {text: 'Serbia and Montenegro', value: '2ddb6d46-a33d-ee11-bdf4-0022489ba618'},
    {text: 'East Timor', value: 'c4d08f7b-a33d-ee11-bdf4-0022489baa41'},
    {text: 'Dutch Antilles', value: '1fe5b62f-a33d-ee11-bdf4-0022489baaeb'}
  ].sort((a, b) => a.text.localeCompare(b.text)))

  /**
   * Industry type dropdown options (first dropdown)
   * Main industry categorization: Automotive, Non-Automotive, Automotive Aftermarket
   */
  const industryTypeOptions = computed(() => {
    const options = [
      {text: i18n.tv('industryAutomotive', 'partnerHub'), value: INDUSTRY_TYPE_CODES.Automotive},
      {text: i18n.tv('industryNonAutomotive', 'partnerHub'), value: INDUSTRY_TYPE_CODES.NonAutomotive},
      {text: i18n.tv('industryAutomotiveAftermarket', 'partnerHub'), value: INDUSTRY_TYPE_CODES.AutomotiveAftermarket}
    ]
    return options
  })

  /**
   * Specific industry dropdown options (second dropdown)
   * Shown only when Non-Automotive is selected
   * Includes 28 detailed industries from Aerospace to Tunnel Boring
   */
  const specificIndustryOptions = computed(() => [
    {text: i18n.tv('industryAerospace', 'partnerHub'), value: SPECIFIC_INDUSTRY_CODES.Aerospace},
    {text: i18n.tv('industryAgriculture', 'partnerHub'), value: SPECIFIC_INDUSTRY_CODES.Agriculture},
    {text: i18n.tv('industryAppliances', 'partnerHub'), value: SPECIFIC_INDUSTRY_CODES.Appliances},
    {text: i18n.tv('industryAutomotive', 'partnerHub'), value: SPECIFIC_INDUSTRY_CODES.Automotive},
    {text: i18n.tv('industryAutomotiveAftermarket', 'partnerHub'), value: SPECIFIC_INDUSTRY_CODES.AutomotiveAftermarket},
    {text: i18n.tv('industryBuildingEngineering', 'partnerHub'), value: SPECIFIC_INDUSTRY_CODES.BuildingEngineering},
    {text: i18n.tv('industryChemical', 'partnerHub'), value: SPECIFIC_INDUSTRY_CODES.Chemical},
    {text: i18n.tv('industryCommercialMarine', 'partnerHub'), value: SPECIFIC_INDUSTRY_CODES.CommercialMarine},
    {text: i18n.tv('industryConstruction', 'partnerHub'), value: SPECIFIC_INDUSTRY_CODES.Construction},
    {text: i18n.tv('industryDistribution', 'partnerHub'), value: SPECIFIC_INDUSTRY_CODES.Distribution},
    {text: i18n.tv('industryEnergyGeneration', 'partnerHub'), value: SPECIFIC_INDUSTRY_CODES.EnergyGeneration},
    {text: i18n.tv('industryEnergyStorage', 'partnerHub'), value: SPECIFIC_INDUSTRY_CODES.EnergyStorage},
    {text: i18n.tv('industryEnergyTransmission', 'partnerHub'), value: SPECIFIC_INDUSTRY_CODES.EnergyTransmission},
    {text: i18n.tv('industryFluidHandling', 'partnerHub'), value: SPECIFIC_INDUSTRY_CODES.FluidHandling},
    {text: i18n.tv('industryFluidTechnology', 'partnerHub'), value: SPECIFIC_INDUSTRY_CODES.FluidTechnology},
    {text: i18n.tv('industryFoodBeverage', 'partnerHub'), value: SPECIFIC_INDUSTRY_CODES.FoodBeverage},
    {text: i18n.tv('industryForklifts', 'partnerHub'), value: SPECIFIC_INDUSTRY_CODES.Forklifts},
    {text: i18n.tv('industryHydrogen', 'partnerHub'), value: SPECIFIC_INDUSTRY_CODES.Hydrogen},
    {text: i18n.tv('industryIndustrialMachines', 'partnerHub'), value: SPECIFIC_INDUSTRY_CODES.IndustrialMachines},
    {text: i18n.tv('industryMetalProcessing', 'partnerHub'), value: SPECIFIC_INDUSTRY_CODES.MetalProcessing},
    {text: i18n.tv('industryPharmaceutical', 'partnerHub'), value: SPECIFIC_INDUSTRY_CODES.Pharmaceutical},
    {text: i18n.tv('industryPowerTools', 'partnerHub'), value: SPECIFIC_INDUSTRY_CODES.PowerTools},
    {text: i18n.tv('industryPowerGeneration', 'partnerHub'), value: SPECIFIC_INDUSTRY_CODES.PowerGeneration},
    {text: i18n.tv('industryPowerTransmission', 'partnerHub'), value: SPECIFIC_INDUSTRY_CODES.PowerTransmission},
    {text: i18n.tv('industryRailway', 'partnerHub'), value: SPECIFIC_INDUSTRY_CODES.Railway},
    {text: i18n.tv('industryRecreational', 'partnerHub'), value: SPECIFIC_INDUSTRY_CODES.Recreational},
    {text: i18n.tv('industryRobotics', 'partnerHub'), value: SPECIFIC_INDUSTRY_CODES.Robotics},
    {text: i18n.tv('industryTunnelBoring', 'partnerHub'), value: SPECIFIC_INDUSTRY_CODES.TunnelBoring}
  ])

  /**
   * Lead Market dropdown options
   * Includes: EMEA, North America, APAC, LATAM
   * Typically shown conditionally when industry is Non-Automotive
   */
  const leadMarketOptions = computed(() => [
    {text: i18n.tv('leadMarketEMEA', 'partnerHub'), value: dynamicsCapture.LEAD_MARKET_CODES.EMEA},
    {text: i18n.tv('leadMarketNA', 'partnerHub'), value: dynamicsCapture.LEAD_MARKET_CODES.NA},
    {text: i18n.tv('leadMarketAPAC', 'partnerHub'), value: dynamicsCapture.LEAD_MARKET_CODES.APAC},
    {text: i18n.tv('leadMarketLATAM', 'partnerHub'), value: dynamicsCapture.LEAD_MARKET_CODES.LATAM}
  ])

  /**
   * Language dropdown options (full set)
   * Includes: English, German, French, Italian, Spanish
   * Use for forms that support all languages
   * Uses the same translations as the language switcher in MetaHeader
   */
  const languageOptions = computed(() => [
    {text: i18n.tv('en', 'locales'), value: 'en'},
    {text: i18n.tv('de', 'locales'), value: 'de'},
    {text: i18n.tv('fr', 'locales'), value: 'fr'},
    {text: i18n.tv('it', 'locales'), value: 'it'},
    {text: i18n.tv('es', 'locales'), value: 'es'}
  ])

  /**
   * Limited language dropdown options (for O-Ring PDF downloads)
   * Includes: English, German only
   * Use for forms with limited language support
   * Uses the same translations as the language switcher in MetaHeader
   */
  const limitedLanguageOptions = computed(() => [
    {text: i18n.tv('en', 'locales'), value: 'en'},
    {text: i18n.tv('de', 'locales'), value: 'de'}
  ])

  /**
   * Helper to check if specific industry dropdown should be shown
   * Returns true when Non-Automotive is selected in industry type dropdown
   * 
   * @param industryTypeValue - Current industry type dropdown value
   */
  const shouldShowSpecificIndustry = (industryTypeValue: number | string | null | undefined): boolean => {
    if (!industryTypeValue) {
      return false
    }
    const numValue = Number(industryTypeValue)
    const result = numValue === INDUSTRY_TYPE_CODES.NonAutomotive
    return result
  }

  /**
   * Helper to check if Lead Market field should be shown
   * Returns true when Non-Automotive is selected in industry type dropdown
   * 
   * @param industryTypeValue - Current industry type dropdown value
   */
  const shouldShowLeadMarket = (industryTypeValue: number | string | null | undefined): boolean => {
    if (!industryTypeValue) {
      return false
    }
    // Handle both number and string comparisons (in case value comes as string from form)
    return Number(industryTypeValue) === INDUSTRY_TYPE_CODES.NonAutomotive
  }

  return {
    countryOptions,
    industryTypeOptions,
    specificIndustryOptions,
    leadMarketOptions,
    languageOptions,
    limitedLanguageOptions,
    shouldShowSpecificIndustry,
    shouldShowLeadMarket,
    INDUSTRY_TYPE_CODES,
    SPECIFIC_INDUSTRY_CODES
  }
}
